import { useReducer, useState, type ChangeEvent, type FormEvent } from 'react';
import { useUser } from '@clerk/clerk-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useSpaceScope } from '@/contexts/SpaceScopeContext';
import { useInviteToSpace, useRevokeSpaceInvite } from '@/hooks/queries/useSpaceInvitations';
import { ApiError } from '@/lib/api';
import { inviteEmailError, MAX_SPACE_PEOPLE, parsePercentage } from '@/lib/spaces/creation';
import type { SpacePage } from '@/lib/spaces/navigation';
import { track } from '@/lib/telemetry';
import type { SpaceDetail, SpaceInvite, SpaceRole } from '@/types/spaces';
import { InviteActionError } from './SpaceInvitations';

export function SpaceInviteManagement({ page }: { page: SpacePage }) {
  const { space } = useSpaceScope();
  if (!space || page !== 'settings') return null;
  const renderInvite = (invite: SpaceInvite) => <PendingInviteRow key={invite.id} invite={invite} space={space} />;
  return <section className="space-y-4 rounded-3xl border border-border-subtle bg-surface p-4 shadow-sm sm:p-5" aria-labelledby="space-invites-title">
    <h2 id="space-invites-title" className="font-mono text-2xs font-semibold uppercase tracking-widest text-text-secondary">Convites pendentes</h2>
    <PendingInviteEmpty empty={!space.pendingInvites.length} />
    <ul className="divide-y divide-border-subtle">{space.pendingInvites.map(renderInvite)}</ul>
    <InvitePersonForm space={space} />
  </section>;
}

function PendingInviteEmpty({ empty }: { empty: boolean }) {
  if (!empty) return null;
  return <p className="text-sm text-text-secondary">Nenhum convite pendente.</p>;
}

function PendingInviteRow({ invite, space }: { invite: SpaceInvite; space: SpaceDetail }) {
  const { trigger, isMutating } = useRevokeSpaceInvite(space.id, invite.id);
  const [error, setError] = useState<string | null>(null);
  const handleRevoke = async () => {
    setError(null);
    try { await trigger(); toast.success('Convite retirado.'); }
    catch { const message = 'Não foi possível retirar o convite. Tente novamente.'; setError(message); toast.error(message); }
  };
  return <li className="flex flex-wrap items-center justify-between gap-3 py-3" aria-busy={isMutating}>
    <div className="min-w-0"><p className="break-words text-sm font-medium text-text-primary">{invite.name ?? invite.email ?? 'Pessoa convidada'}</p><p className="text-xs text-text-secondary">{invite.pct === null ? 'Divisão ao entrar' : `${invite.pct.toLocaleString('pt-BR')}%`} · {invite.role === 'editor' ? 'Editor' : 'Leitura'}</p><InviteActionError error={error} /></div>
    <WithdrawInvite allowed={space.myRole === 'editor' && !space.archivedAt} busy={isMutating} onWithdraw={handleRevoke} />
  </li>;
}

function WithdrawInvite({ allowed, busy, onWithdraw }: { allowed: boolean; busy: boolean; onWithdraw: () => void }) {
  if (!allowed) return null;
  return <Button type="button" variant="outline" size="sm" disabled={busy} onClick={onWithdraw}>Retirar convite</Button>;
}

type InviteFormError = { field: 'email' | 'pct' | null; message: string };
type InviteFormState = { email: string; pct: string; role: SpaceRole; error: InviteFormError | null };
type InviteFormAction =
  | { type: 'email' | 'pct'; value: string }
  | { type: 'role'; value: SpaceRole }
  | { type: 'error'; error: InviteFormError }
  | { type: 'reset' };
const EMPTY_INVITE_FORM: InviteFormState = { email: '', pct: '', role: 'editor', error: null };

function inviteFormReducer(state: InviteFormState, action: InviteFormAction): InviteFormState {
  if (action.type === 'reset') return EMPTY_INVITE_FORM;
  if (action.type === 'error') return { ...state, error: action.error };
  const error = state.error?.field && state.error.field !== action.type ? state.error : null;
  return { ...state, [action.type]: action.value, error };
}

function validateInviteForm(form: InviteFormState, existingEmails: string[]): InviteFormError | null {
  const emailError = inviteEmailError(form.email, existingEmails);
  if (emailError) return { field: 'email', message: emailError };
  if (form.pct.trim() && parsePercentage(form.pct) === null) {
    return { field: 'pct', message: 'Use um percentual de 0 a 100, com até 2 casas decimais.' };
  }
  return null;
}

function InvitePersonForm({ space }: { space: SpaceDetail }) {
  const { user } = useUser();
  const mutation = useInviteToSpace(space.id);
  const [form, dispatch] = useReducer(inviteFormReducer, EMPTY_INVITE_FORM);
  if (space.myRole !== 'editor' || space.archivedAt) return null;
  if (space.members.length + space.pendingInvites.length >= MAX_SPACE_PEOPLE) return <p className="text-sm text-text-secondary">O limite de 6 pessoas inclui os convites pendentes.</p>;
  const handleEmail = (event: ChangeEvent<HTMLInputElement>) => dispatch({ type: 'email', value: event.target.value });
  const handlePct = (event: ChangeEvent<HTMLInputElement>) => dispatch({ type: 'pct', value: event.target.value });
  const handleRole = (event: ChangeEvent<HTMLSelectElement>) => dispatch({ type: 'role', value: event.target.value === 'viewer' ? 'viewer' : 'editor' });
  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (mutation.isMutating) return;
    const pct = form.pct.trim() ? parsePercentage(form.pct) : null;
    const existingEmails = [
      ...(user?.emailAddresses.map((address) => address.emailAddress) ?? []),
      ...[...space.members, ...space.pendingInvites].map((person) => person.email ?? ''),
    ];
    const error = validateInviteForm(form, existingEmails);
    if (error) { dispatch({ type: 'error', error }); return; }
    try {
      await mutation.trigger({ email: form.email.trim().toLowerCase(), pct, role: form.role });
      track('space.invite.sent', { source: 'settings', role: form.role });
      dispatch({ type: 'reset' });
      toast.success('Convite disponível na Vértice.');
    } catch (cause) {
      const message = cause instanceof ApiError && [400, 403, 409].includes(cause.status) ? cause.message : 'Não foi possível convidar. Tente novamente.';
      dispatch({ type: 'error', error: { field: null, message } }); toast.error(message);
    }
  };
  return <form onSubmit={handleSubmit} noValidate aria-busy={mutation.isMutating} className="border-t border-border-subtle pt-4">
    <fieldset disabled={mutation.isMutating} className="space-y-3"><legend className="text-sm font-semibold text-text-primary">Convidar alguém que já usa a Vértice</legend>
      <div><label htmlFor="settings-invite-email" className="text-xs text-text-secondary">E-mail</label><Input id="settings-invite-email" type="email" value={form.email} onChange={handleEmail} maxLength={320} autoComplete="off" aria-invalid={form.error?.field === 'email'} aria-describedby={form.error?.field === 'email' ? 'settings-invite-error' : undefined} /></div>
      <div className="grid gap-3 sm:grid-cols-2"><div><label htmlFor="settings-invite-pct" className="text-xs text-text-secondary">Percentual (opcional)</label><Input id="settings-invite-pct" inputMode="decimal" value={form.pct} onChange={handlePct} aria-invalid={form.error?.field === 'pct'} aria-describedby={form.error?.field === 'pct' ? 'settings-invite-pct-hint settings-invite-error' : 'settings-invite-pct-hint'} /><p id="settings-invite-pct-hint" className="mt-1 text-xs text-text-secondary">Em branco: usa a média ao entrar.</p></div><div><label htmlFor="settings-invite-role" className="text-xs text-text-secondary">Permissão</label><select id="settings-invite-role" value={form.role} onChange={handleRole} className="h-10 w-full cursor-pointer rounded-md border border-border-subtle bg-surface px-3 text-sm text-text-primary focus-visible:outline-2 focus-visible:outline-ring"><option value="editor">Editor</option><option value="viewer">Leitura</option></select></div></div>
      <p className="text-xs text-text-secondary">A pessoa responde dentro da Vértice. Nenhum e-mail ou mensagem é enviado.</p>
      <div id="settings-invite-error"><InviteActionError error={form.error?.message ?? null} /></div>
      <Button type="submit" disabled={mutation.isMutating}>{mutation.isMutating ? 'Convidando…' : 'Convidar'}</Button>
    </fieldset>
  </form>;
}
